import { apiDelete, apiGet, apiPatch, apiPost } from './api'
import {
  ADMIN_BILLING_ASSIGN_PLAN,
  ADMIN_BILLING_METRICS,
  ADMIN_BILLING_OVERRIDES,
  ADMIN_BILLING_OVERRIDE_BY_ID,
  ADMIN_BILLING_OVERRIDES_BY_BUSINESS,
  ADMIN_BUSINESS_DETAIL,
  ADMIN_OVERVIEW,
  ADMIN_BILLING_SUBSCRIPTION_CANCEL,
  ADMIN_BILLING_SUBSCRIPTION_PLAN,
  ADMIN_BILLING_SUBSCRIPTION_REFUND,
  ADMIN_BILLING_SUBSCRIPTIONS,
  ADMIN_BUSINESSES,
  ADMIN_BUSINESSES_SEARCH,
  ADMIN_USERS,
  BILLING_CANCEL,
  BILLING_CHECKOUT,
  BILLING_CURRENT,
  BILLING_INVOICES,
  BILLING_PLANS,
  BILLING_PORTAL,
  BILLING_RESUME,
} from './endpoints'
import type {
  AdminBusinessDetail,
  AdminBusinessResult,
  AdminBusinessRow,
  AdminOverview,
  AdminEntitlementOverride,
  AdminSubscriptionRow,
  AdminUserRow,
  BillingCycle,
  BillingMetrics,
  BillingPortalResponse,
  CheckoutSessionResponse,
  CurrentSubscriptionResponse,
  FeatureKey,
  Invoice,
  PaginatedResponse,
  Plan,
  PlanCode,
  SubscriptionStatus,
} from '../types/api'

export function fetchPlans(): Promise<Plan[]> {
  return apiGet<Plan[]>(BILLING_PLANS)
}

export function fetchCurrentSubscription(): Promise<CurrentSubscriptionResponse> {
  return apiGet<CurrentSubscriptionResponse>(BILLING_CURRENT)
}

export function startCheckout(
  planCode: PlanCode,
  cycle: BillingCycle,
  units?: number,
): Promise<CheckoutSessionResponse> {
  return apiPost<CheckoutSessionResponse, { planCode: PlanCode; billingCycle: BillingCycle; units?: number }>(
    BILLING_CHECKOUT,
    { planCode, billingCycle: cycle, units },
  )
}

export function openBillingPortal(): Promise<BillingPortalResponse> {
  return apiPost<BillingPortalResponse, Record<string, never>>(BILLING_PORTAL, {})
}

export function cancelSubscription(atPeriodEnd: boolean): Promise<void> {
  return apiPost<void, { atPeriodEnd: boolean }>(BILLING_CANCEL, { atPeriodEnd })
}

export function resumeSubscription(): Promise<void> {
  return apiPost<void, Record<string, never>>(BILLING_RESUME, {})
}

export function listInvoices(page: number, size: number): Promise<PaginatedResponse<Invoice>> {
  const params = new URLSearchParams({ page: String(page), size: String(size) })
  return apiGet<PaginatedResponse<Invoice>>(`${BILLING_INVOICES}?${params.toString()}`)
}

// Admin

export function fetchBillingMetrics(): Promise<BillingMetrics> {
  return apiGet<BillingMetrics>(ADMIN_BILLING_METRICS)
}

export function listAdminSubscriptions(params: {
  status?: SubscriptionStatus
  page: number
  size: number
}): Promise<PaginatedResponse<AdminSubscriptionRow>> {
  const qs = new URLSearchParams({ page: String(params.page), size: String(params.size) })
  if (params.status) qs.set('status', params.status)
  return apiGet<PaginatedResponse<AdminSubscriptionRow>>(`${ADMIN_BILLING_SUBSCRIPTIONS}?${qs.toString()}`)
}

export function refundSubscription(id: string, amountCents: number, reason: string): Promise<void> {
  return apiPost<void, { amountCents: number; reason: string }>(
    ADMIN_BILLING_SUBSCRIPTION_REFUND(id),
    { amountCents, reason },
  )
}

export function adminCancelSubscription(id: string, reason: string): Promise<void> {
  return apiDelete<void>(ADMIN_BILLING_SUBSCRIPTION_CANCEL(id) + `?reason=${encodeURIComponent(reason)}`)
}

export function adminChangePlan(id: string, planCode: PlanCode, billingCycle: BillingCycle, countInRevenue: boolean): Promise<AdminSubscriptionRow> {
  return apiPatch<AdminSubscriptionRow, { planCode: PlanCode; billingCycle: BillingCycle; countInRevenue: boolean }>(
    ADMIN_BILLING_SUBSCRIPTION_PLAN(id),
    { planCode, billingCycle, countInRevenue },
  )
}

export function listAdminBusinesses(params: {
  page: number
  size: number
  planCode?: string
  status?: string
  q?: string
}): Promise<PaginatedResponse<AdminBusinessRow>> {
  const qs = new URLSearchParams({ page: String(params.page), size: String(params.size) })
  if (params.planCode) qs.set('planCode', params.planCode)
  if (params.status)   qs.set('status', params.status)
  if (params.q)        qs.set('q', params.q)
  return apiGet<PaginatedResponse<AdminBusinessRow>>(`${ADMIN_BUSINESSES}?${qs.toString()}`)
}

export function searchAdminBusinesses(q: string): Promise<AdminBusinessResult[]> {
  return apiGet<AdminBusinessResult[]>(ADMIN_BUSINESSES_SEARCH(q))
}

export function adminAssignPlan(
  businessId: string,
  planCode: PlanCode,
  billingCycle: BillingCycle,
  countInRevenue: boolean,
): Promise<AdminSubscriptionRow> {
  return apiPost<AdminSubscriptionRow, { businessId: string; planCode: PlanCode; billingCycle: BillingCycle; countInRevenue: boolean }>(
    ADMIN_BILLING_ASSIGN_PLAN,
    { businessId, planCode, billingCycle, countInRevenue },
  )
}

export function createEntitlementOverride(input: {
  businessId: string
  featureKey: FeatureKey
  boolValue?: boolean
  intValue?: number
  expiresAt?: string
  reason?: string
}): Promise<void> {
  return apiPost<void, typeof input>(ADMIN_BILLING_OVERRIDES, input)
}

export function fetchAdminBusinessDetail(businessId: string): Promise<AdminBusinessDetail> {
  return apiGet<AdminBusinessDetail>(ADMIN_BUSINESS_DETAIL(businessId))
}

export function fetchAdminOverview(): Promise<AdminOverview> {
  return apiGet<AdminOverview>(ADMIN_OVERVIEW)
}

export function listEntitlementOverridesForBusiness(businessId: string): Promise<AdminEntitlementOverride[]> {
  return apiGet<AdminEntitlementOverride[]>(ADMIN_BILLING_OVERRIDES_BY_BUSINESS(businessId))
}

export function revokeEntitlementOverride(overrideId: string): Promise<void> {
  return apiDelete<void>(ADMIN_BILLING_OVERRIDE_BY_ID(overrideId))
}

export function listAdminUsers(params: {
  page: number
  size: number
  hasBusinessProfile?: boolean | null
  q?: string
}): Promise<import('../types/api').PaginatedResponse<AdminUserRow>> {
  const qs = new URLSearchParams()
  qs.set('page', String(params.page))
  qs.set('size', String(params.size))
  if (params.hasBusinessProfile != null) qs.set('hasBusinessProfile', String(params.hasBusinessProfile))
  if (params.q) qs.set('q', params.q)
  return apiGet<import('../types/api').PaginatedResponse<AdminUserRow>>(`${ADMIN_USERS}?${qs.toString()}`)
}
